import { Badge, Button, SkeletonLoader } from '@postscript/components';
import { PROPOSAL_DESCRIPTION_UNAVAILABLE } from 'components/brand/offers/constants';
import {
  hasVerificationConflicts,
  NEEDS_REVIEW_LABEL,
  programmeSummary,
  proposalDescription,
} from 'components/brand/offers/offerScanPresentation';
import { useProposalAudience } from 'components/brand/offers/proposalAudience';
import { ProposalVerificationBadge } from 'components/brand/offers/ProposalVerification';
import { MutedText } from 'components/brand/offers/proposalStyles';
import {
  LLM_CORRESPONDENCE_MATCH_RULE,
  OfferProposal,
  ProposalDisposition,
  PromotionGroup,
} from 'components/brand/offers/types';
import styled from 'styled-components';
import { ProposalScanDetails } from './ProposalScanDetails';

export const ProposalList = styled.ul`
  list-style: none;
  display: grid;
  gap: var(--spacing-2);
  margin: var(--spacing-2) 0 0;
  padding: 0;
`;

export const ProposalRow = styled.li`
  display: grid;
  gap: var(--spacing-2);
  padding: var(--spacing-2);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-medium);
`;

const CampaignSummary = styled.div`
  display: grid;
  gap: var(--spacing-1);
  padding-bottom: var(--spacing-2);
  border-bottom: 1px solid var(--border-color);
`;

const CampaignCarriers = styled.div`
  display: grid;
  gap: var(--spacing-2);
`;

const CampaignCarrier = styled.div`
  display: grid;
  gap: var(--spacing-2);

  & + & {
    padding-top: var(--spacing-2);
    border-top: 1px solid var(--border-color);
  }
`;

const RowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-2);
  width: 100%;
  text-align: left;
  font: inherit;
`;

const RowSummary = styled.span`
  display: grid;
  gap: var(--spacing-1);
  min-width: 0;
`;

const RowTitle = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  min-width: 0;
`;

const RowBadges = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  flex-shrink: 0;
`;

const RowActions = styled.span`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  flex-shrink: 0;
`;

const OfferName = styled.span`
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const OfferValue = styled.span`
  color: var(--gray-6);
`;

const SkeletonRows = styled.div`
  display: grid;
  gap: var(--spacing-2);
  margin-top: var(--spacing-2);
`;

export const ProposalListSkeleton = (): JSX.Element => (
  <SkeletonRows aria-label="Loading scanned offers" role="status">
    {[0, 1, 2].map((row) => (
      <SkeletonLoader
        key={row}
        width="full"
        height={64}
        borderRadius="medium"
      />
    ))}
  </SkeletonRows>
);

const DISPOSITION_PRESENTATION = {
  [ProposalDisposition.NEW]: { label: 'New offer', variant: 'info' },
  [ProposalDisposition.ENRICH]: {
    label: 'Update to existing offer',
    variant: 'success',
  },
  [ProposalDisposition.POSSIBLE_MATCH]: {
    label: 'Possible match',
    variant: 'warning',
  },
  [ProposalDisposition.CONFLICTED]: {
    label: 'Conflicting terms',
    variant: 'error',
  },
} as const;

const count = (n: number, singular: string, plural = `${singular}s`): string =>
  `${n} ${n === 1 ? singular : plural}`;

const pagesSeenLabel = (pagesSeenOn?: number | null): string | null => {
  if (typeof pagesSeenOn !== 'number' || pagesSeenOn < 1) return null;
  return `On ${count(pagesSeenOn, 'page')}`;
};

const proposalTitle = (proposal: OfferProposal): string =>
  proposal.consolidatedOfferName ?? proposal.offerName;

interface ProposalHeadlineProps {
  proposal: OfferProposal;
  showValue?: boolean;
}

export const ProposalHeadline = ({
  proposal,
  showValue = false,
}: ProposalHeadlineProps): JSX.Element => {
  const consolidatedName = proposal.consolidatedOfferName;
  const pagesSeen = pagesSeenLabel(proposal.pagesSeenOn);
  const programme = programmeSummary(proposal.verification);
  const scope =
    proposal.consolidation?.applicability?.scope ??
    proposal.scopeAndEligibility;
  const summary =
    proposalDescription(proposal).split(/\n\s*\n/)[0] ||
    PROPOSAL_DESCRIPTION_UNAVAILABLE;

  return (
    <RowSummary>
      <RowTitle>
        <OfferName>{consolidatedName ?? proposal.offerName}</OfferName>
        {(!consolidatedName || showValue) && (
          <OfferValue>{proposal.value}</OfferValue>
        )}
        {programme && <MutedText>{programme}</MutedText>}
        {pagesSeen && <MutedText>{pagesSeen}</MutedText>}
      </RowTitle>
      {showValue && scope && <MutedText>{scope}</MutedText>}
      <MutedText>{summary}</MutedText>
    </RowSummary>
  );
};

const ProposalBadges = ({
  proposal,
}: {
  proposal: OfferProposal;
}): JSX.Element => {
  const audience = useProposalAudience();
  const presentation =
    DISPOSITION_PRESENTATION[
      proposal.disposition as keyof typeof DISPOSITION_PRESENTATION
    ];
  const needsCheck =
    audience === 'merchant' && hasVerificationConflicts(proposal);
  // An unverified tier already reads "Needs review" for merchants.
  const showTier = !(needsCheck && !proposal.verificationTier);

  return (
    <RowBadges>
      {showTier && (
        <ProposalVerificationBadge tier={proposal.verificationTier} />
      )}
      {needsCheck && (
        <Badge size="small" variant="warning">
          {NEEDS_REVIEW_LABEL}
        </Badge>
      )}
      {proposal.matchRule === LLM_CORRESPONDENCE_MATCH_RULE && (
        <Badge size="small" variant="warning">
          Possible existing offer
        </Badge>
      )}
      {presentation && (
        <Badge size="small" variant={presentation.variant}>
          {proposal.targetKind === 'oms_offer'
            ? 'Existing offer match'
            : presentation.label}
        </Badge>
      )}
    </RowBadges>
  );
};

interface ProposalGroupListProps {
  groups: PromotionGroup[];
  onReview: (proposal: OfferProposal, sightingCount: number) => void;
  /** Renders a Dismiss action on every row when set. */
  onDismiss?: (proposal: OfferProposal) => void;
  dismissingId?: number | null;
  /** Marks the row whose review is open, for the queue's inline review. */
  expandedProposalId?: number | null;
  showScanDetails?: boolean;
  'aria-label'?: string;
}

export const ProposalGroupList = ({
  groups,
  onReview,
  onDismiss,
  dismissingId = null,
  expandedProposalId,
  showScanDetails = false,
  'aria-label': ariaLabel,
}: ProposalGroupListProps): JSX.Element => (
  <ProposalList aria-label={ariaLabel}>
    {groups.map((group) => {
      const representative = group.proposals[0];
      const carriers = group.campaignLabel ? group.proposals : [representative];
      const isCampaign = carriers.length > 1;
      // Several actions per row, or several rows per campaign, need names.
      const nameActions = isCampaign || Boolean(onDismiss);

      return (
        <ProposalRow key={group.promotionKey}>
          {isCampaign && (
            <CampaignSummary>
              <OfferName>{group.campaignLabel}</OfferName>
              <MutedText>
                {count(carriers.length, 'benefit')} found for this campaign
              </MutedText>
            </CampaignSummary>
          )}
          <CampaignCarriers>
            {carriers.map((proposal) => {
              const sightingCount = isCampaign
                ? Math.max(proposal.duplicateCount, 1)
                : group.sightingCount;
              const isDismissing = dismissingId === proposal.id;

              return (
                <CampaignCarrier key={proposal.id}>
                  <RowHeader>
                    <ProposalHeadline
                      proposal={proposal}
                      showValue={isCampaign}
                    />
                    <RowActions>
                      <ProposalBadges proposal={proposal} />
                      {onDismiss && (
                        <Button
                          type="button"
                          variant="text"
                          size="small"
                          aria-label={`Dismiss ${proposalTitle(proposal)}`}
                          loading={isDismissing}
                          disabled={dismissingId !== null}
                          onClick={() => onDismiss(proposal)}
                        >
                          Dismiss
                        </Button>
                      )}
                      <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        aria-label={
                          nameActions
                            ? `Review ${proposalTitle(proposal)}`
                            : undefined
                        }
                        aria-expanded={
                          expandedProposalId === undefined
                            ? undefined
                            : expandedProposalId === proposal.id
                        }
                        disabled={isDismissing}
                        onClick={() => onReview(proposal, sightingCount)}
                      >
                        Review
                      </Button>
                    </RowActions>
                  </RowHeader>
                  {showScanDetails && (
                    <ProposalScanDetails proposal={proposal} />
                  )}
                </CampaignCarrier>
              );
            })}
          </CampaignCarriers>
        </ProposalRow>
      );
    })}
  </ProposalList>
);
